import React from 'react';
import { useEffect, useRef } from 'react';
import '../styles/SellSurplus.css';
import offerImage from '../assets/offer-image.png';
import seamlessImage from '../assets/seamless-image.png';
import valuationImage from '../assets/valuation-image.png';
import professionalImage from '../assets/professional-image.png';
import eqImg from '../assets/equipment-types.png';
import clientsImg from '../assets/our-clients.png'
import OfferForm from './OfferForm';
import Footer from './Footer';


export default function SellSurplus() {

  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // scroll helper for the “Get Started” CTA
  const scrollToGetStarted = () => {
    document
      .getElementById('get-started')
      .scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="sell-surplus-container">

        {/* Sell Surplus — metadata */}
        <title>We Buy Surplus Industrial Equipment — Fast Quotes & Nationwide Pickup</title>
        <meta
          name="description"
          content="Sell surplus transformers, switchgear, PVF and electrical equipment. Fast quotes, insured nationwide pickup, and payment in 24–48h. Get a guaranteed offer."
        />
        <link rel="canonical" href="https://sdindustrialsurplus.com/sell-surplus" />
        <meta name="robots" content="index,follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sdindustrialsurplus.com/sell-surplus" />
        <meta property="og:site_name" content="S&D Industrial Surplus" />
        <meta property="og:title" content="We Buy Surplus Industrial Equipment — Fast Quotes & Pickup" />
        <meta property="og:description" content="Transparent pricing, insured logistics, and 24–48h payment. Upload your list or call to get your guaranteed offer." />
        <meta property="og:image" content="https://sdindustrialsurplus.com/logo192.png" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="We Buy Surplus Industrial Equipment — Fast Quotes & Pickup" />
        <meta name="twitter:description" content="Transparent pricing, insured logistics, and 24–48h payment. Upload your list or call to get your guaranteed offer." />
        <meta name="twitter:image" content="https://sdindustrialsurplus.com/logo192.png" />

        {/* Optional: Service schema for this page */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Surplus Industrial Equipment Purchasing",
          "serviceType": "We buy surplus transformers, switchgear, PVF, and related electrical equipment",
          "areaServed": "US",
          "provider": {
            "@type": "Organization",
            "name": "S&D Industrial Surplus",
            "url": "https://sdindustrialsurplus.com/"
          },
          "url": "https://sdindustrialsurplus.com/sell-surplus"
        })}</script>

        <GetOfferCTA onOfferClick={scrollToGetStarted}/>
        <HowItWorks />
        <div className='sbs-wrapper'>
            <EquipmentTypes/>
            <WhoWeBuyFrom />
        </div>
        <OfferForm />
        <Footer />
    </div>
  );
}


function GetOfferCTA({onOfferClick}) {
    return (
      <section className="get-offer-cta">
        <div className="offer-left">
          <h1>We Buy Surplus Industrial Equipment, Nationwide Pickup</h1>
          <p>
            We’ll send you a no-obligation quote fast, just tell us what you have,
            and we’ll handle the rest. Click on get an offer and fill out the inquiry form or call toll free at <a href="tel:8328852369" className="phone-link"> (832) 885-2369</a>.
          </p>
          <span className="btn-offer-wrap">
            <button className="btn-offer" onClick={onOfferClick}>
              Get an offer
            </button>
          </span>
        </div>
        <div className="offer-right">
          <img src={offerImage} data-smart-multimime="1" alt="Illustration of equipment quote" />
        </div>
      </section>
    );
  }

function HowItWorks() {
  const railRef = useRef(null);

  // Pure drag-to-scroll (NO momentum), doesn't trap page scroll
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let activePointerId = null;

    const clampScroll = (val) => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      return Math.min(Math.max(0, val), max);
    };

    const getX = (e) => e.clientX ?? e.touches?.[0]?.clientX ?? 0;

    const onDown = (e) => {
      isDown = true;
      moved = false;
      activePointerId = e.pointerId ?? null;
      startX = getX(e);
      startLeft = el.scrollLeft;

      // Smooth drag UX without blocking page scroll:
      // avoid default text selection by temporarily disabling it
      el.style.userSelect = 'none';
      el.classList.add('is-dragging'); // keep cursor as "grab", not "grabbing"
      el.setPointerCapture?.(activePointerId);

      // Prevent selection start; don't block wheel/scroll on the page
      if (e.cancelable) e.preventDefault();
    };

    const onMove = (e) => {
      if (!isDown) return;
      const x = getX(e);
      const dx = x - startX;
      el.scrollLeft = clampScroll(startLeft - dx);
      if (Math.abs(dx) > 2) moved = true;
      // No preventDefault here—lets the page still process natural scroll elsewhere
    };

    const releaseCapture = () => {
      if (activePointerId != null && el.hasPointerCapture?.(activePointerId)) {
        el.releasePointerCapture(activePointerId);
      }
      activePointerId = null;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.userSelect = '';
      el.classList.remove('is-dragging');
      releaseCapture();
      // No momentum/inertia—stop instantly
    };

    const onLeave = () => { if (isDown) onUp(); };

    // Swallow clicks only if we actually dragged
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('pointerdown', onDown, { passive: false });
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerup', onUp, { passive: true });
    el.addEventListener('pointercancel', onUp, { passive: true });
    el.addEventListener('pointerleave', onLeave, { passive: true });
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.style.userSelect = '';
      el.classList.remove('is-dragging');
      releaseCapture();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  // Only show grab cursor when actual horizontal overflow exists
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    const update = () => {
      const hasOverflow = (el.scrollWidth - el.clientWidth) > 1;
      el.classList.toggle('has-x-overflow', hasOverflow);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });

    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const steps = [
    {
      img: seamlessImage,
      title: '1. Seamless Communication',
      desc: `We keep you in the loop at every step of your industrial transformer, PVF & electrical surplus sale—whether it’s scheduling a pick-up, sending photos, or answering questions, you get instant, convenient updates and support.`,
    },
    {
      img: valuationImage,
      title: '2. Efficient Valuations & Prompt Payments',
      desc: `Our team leverages market data to deliver fast, accurate valuations on your electrical industrial surplus—transformers, switchgear, PVF and more—and issues payments promptly, so you get the best offer without the wait.`,
    },
    {
      img: professionalImage,
      title: '3. Professional Handling of Your Assets',
      desc: `From careful on-site inspection to insured transport, we manage your surplus transformers, PVF piping, and other electrical equipment with top-tier safety protocols and professional handling—no liability, no hassle.`,
    },
  ];

  return (
    <section className="how-it-works-section">
      <h2 className="how-it-works-heading">
        How Our Surplus Equipment Buying Process Works
      </h2>
      {/* Note: no data-lenis-prevent here */}
      <div className="how-it-works-grid" ref={railRef}>
        {steps.map(({ img, title, desc }, i) => (
          <div className="how-it-works-card" key={i}>
            <img src={img} alt="" className="how-it-works-img" />
            <h3 className="how-it-works-title">{title}</h3>
            <p className="how-it-works-desc">{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}




  function EquipmentTypes() {
    const ITEMS = [
      {
        label: 'Transformers',
        note :
          'Padmount · Polemount · Dry-Type · Liquid Filled',
      },
      {
        label: 'PVF',
        note :
          'Industrial pipes · valves · fittings (all sizes & materials)',
      },
      {
        label: 'Electrical Switchgear',
        note :
          'Metal-enclosed · pad-mount · low & medium-voltage',
      },
      {
        label: 'Substation Gear',
        note :
          'Breakers · panelboards · control gear & apparatus',
      },
      {
        label: 'Related Industrial Equipment',
        note : 'Motors · capacitors · relay panels, etc.',
      },
    ];
  
    return (
      <section className="side-by-side-section">
        <div className="sbs-image">
          <img src={eqImg} alt="Variety of industrial equipment" />
        </div>
  
        <div className="sbs-content">
          <h2>What&nbsp;Equipment&nbsp;We&nbsp;Buy</h2>
  
          {/* ★ modern, bullet-free list */}
          <div className="equip-grid">
            {ITEMS.map(({ label, note }) => (
              <div className="equip-item" key={label}>
                <span className="equip-icon" aria-hidden />
                <div className="equip-text">
                  <strong>{label}</strong>
                  <small>{note}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  

  function WhoWeBuyFrom() {
    return (
      <section className="side-by-side-section">
        <div className="sbs-content">
          <h2>Who We Buy From</h2>
          <p>
            We serve utility companies, OEMs, contractors, and facility managers nationwide, large or small. S&D gives you a fast, fair offer and handles every step, from quote to pickup, for transformers, PVF, and switchgear.
          </p>
        </div>
        <div className="sbs-image">
          <img src={clientsImg} alt="Happy industrial clients" />
        </div>
      </section>
    );
  }